import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    consentAllowsAnalytics,
    consentAllowsMarketing,
    HM_CONSENT_UPDATED_EVENT,
} from '../../utils/cookieConsent';

/**
 * Reads SEO bundle cached by App.js from /api/site-settings (localStorage key: seoSettings).
 */
function readSeoSettings() {
    try {
        return JSON.parse(localStorage.getItem('seoSettings') || '{}');
    } catch (e) {
        console.error('Failed to parse SEO settings', e);
        return {};
    }
}

/**
 * Injects GSC verification meta (non-tracking), GA4, Meta Pixel based on tenant SEO settings
 * and visitor cookie consent. Google Tag is bootstrapped in index.html with Consent Mode defaults.
 */
function injectTrackingPixels() {
    const seo = readSeoSettings();
    const {
        googleVerification,
        googleAnalyticsId: gaRaw,
        facebookPixelId: fbRaw,
    } = seo;

    // 1. Google Search Console verification meta (not behavioural advertising)
    if (googleVerification) {
        let metaGSC = document.querySelector('meta[name="google-site-verification"]');
        if (!metaGSC) {
            metaGSC = document.createElement('meta');
            metaGSC.name = 'google-site-verification';
            document.head.appendChild(metaGSC);
        }
        const content = googleVerification.includes('content="')
            ? googleVerification.split('content="')[1].split('"')[0]
            : googleVerification;
        metaGSC.content = content;
    }

    const googleAnalyticsId = typeof gaRaw === 'string' ? gaRaw.trim() : '';
    const allowGa = consentAllowsAnalytics() && !!googleAnalyticsId;
    const allowMarketing = consentAllowsMarketing();

    // 2. Google Analytics (GA4) — only after analytics consent
    if (allowGa) {
        if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', {
                analytics_storage: 'granted',
                ad_storage: allowMarketing ? 'granted' : 'denied',
                ad_user_data: allowMarketing ? 'granted' : 'denied',
                ad_personalization: allowMarketing ? 'granted' : 'denied',
            });
        }

        if (window.__HM_GTAG_MEASUREMENT_ID__ === googleAnalyticsId) {
            /* already loaded this property */
        } else {
            window.__HM_GTAG_MEASUREMENT_ID__ = googleAnalyticsId;
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleAnalyticsId)}`;
            document.head.appendChild(script);

            const inlineScript = document.createElement('script');
            const id = JSON.stringify(googleAnalyticsId);
            const m = allowMarketing;
            inlineScript.text = `
                gtag('js', new Date());
                gtag('consent', 'update', {
                  analytics_storage: 'granted',
                  ad_storage: ${m ? "'granted'" : "'denied'"},
                  ad_user_data: ${m ? "'granted'" : "'denied'"},
                  ad_personalization: ${m ? "'granted'" : "'denied'"},
                });
                gtag('config', ${id});
            `;
            document.head.appendChild(inlineScript);
        }
    }

    const facebookPixelId = typeof fbRaw === 'string' ? fbRaw.trim() : '';

    // 3. Meta Pixel — only after marketing consent
    if (facebookPixelId && allowMarketing && !window.fbq) {
        window.__HM_FB_PIXEL_ID__ = facebookPixelId;
        const fbScript = document.createElement('script');
        fbScript.text = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', ${JSON.stringify(facebookPixelId)});
            fbq('track', 'PageView');
        `;
        document.head.appendChild(fbScript);
    }
}

function applyMetaTemplates(pathname) {
    // The server already rendered this URL's head from the same templates
    // (window.__HM_SEO__); rewriting it here only ever made it worse.
    const served = window.__HM_SEO__;
    if (served && served.path && (String(served.path).replace(/\/+$/, '') || '/') === (String(pathname).replace(/\/+$/, '') || '/')) return;
    const seo = readSeoSettings();
    const { metaTitleTemplate, metaDescriptionTemplate } = seo;
    let siteBrand = {};
    try { siteBrand = JSON.parse(localStorage.getItem('hm_site_brand') || '{}') || {}; } catch { siteBrand = {}; }
    const siteName = siteBrand.siteName || (window.__HM_BRAND__ && window.__HM_BRAND__.siteName) || '';
    if (!siteName) return;

    if (pathname === '/' || !document.title) {
        if (metaTitleTemplate) {
            const t = metaTitleTemplate.split('{{siteName}}').join(siteName)
                .replace(/\{\{page\}\}\s*[|\-–—:]?\s*/g, '').replace(/\s*[|\-–—:]\s*$/, '').trim();
            if (t) document.title = t;
        }
    }

    if (pathname === '/') {
        if (metaDescriptionTemplate) {
            let mDesc = document.querySelector('meta[name="description"]');
            if (!mDesc) {
                mDesc = document.createElement('meta');
                mDesc.name = 'description';
                document.head.appendChild(mDesc);
            }
            mDesc.content = metaDescriptionTemplate.split('{{siteName}}').join(siteName);
        }
    }
}

/**
 * DynamicSEO — tenant meta titles, GSC, GA4, Meta Pixel (consent-gated).
 */
const DynamicSEO = () => {
    const location = useLocation();

    useEffect(() => {
        injectTrackingPixels();

        const timer = setTimeout(() => applyMetaTemplates(location.pathname), 500);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    useEffect(() => {
        const onSettingsLoaded = () => {
            injectTrackingPixels();
            applyMetaTemplates(window.location.pathname);
        };
        const onConsent = () => injectTrackingPixels();
        window.addEventListener('hm-settings-loaded', onSettingsLoaded);
        window.addEventListener(HM_CONSENT_UPDATED_EVENT, onConsent);
        return () => {
            window.removeEventListener('hm-settings-loaded', onSettingsLoaded);
            window.removeEventListener(HM_CONSENT_UPDATED_EVENT, onConsent);
        };
    }, []);

    return null;
};

export default DynamicSEO;
